import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { ArrowLeft, Save, ExternalLink, Image as ImageIcon, Loader2, Plus, Video, Sparkles, RefreshCw, Upload } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Display helper: turn relative /api/blog-images/... into a full URL
function imageDisplayUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
}

/**
 * Shared blog image picker: lets the admin EITHER upload a file from device
 * (sent to Cloudinary via /api/admin/blog/upload-image) OR paste an external
 * URL. Used by both the Edit and Create flows.
 */
function BlogImageUploader({ imageKey, label, value, onChange, slug, testIdPrefix = 'blog-image' }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large (max 10 MB).');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('slug', slug || '');
      formData.append('image_key', imageKey);
      const { data } = await axios.post(`${API_URL}/api/admin/blog/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      onChange({ url: data.url, alt: value.alt || '' });
      toast.success('Image uploaded to Cloudinary');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Upload failed';
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="card-default p-3 space-y-2" data-testid={`${testIdPrefix}-${imageKey}`}>
      <div className="flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-text-secondary" />
        <p className="text-xs font-semibold text-dark">{label}</p>
        {value.url && <span className="text-xs bg-primary-light text-primary px-2 py-0.5 rounded-full ml-auto">Set</span>}
      </div>

      {value.url && (
        <img src={imageDisplayUrl(value.url)} alt={value.alt} className="w-full h-32 object-cover rounded-lg" />
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          data-testid={`${testIdPrefix}-file-input-${imageKey}`}
        />
        <Button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="btn-primary text-xs py-2 px-3 flex-1"
          data-testid={`${testIdPrefix}-upload-${imageKey}`}
        >
          {uploading
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Uploading…</>
            : <><Upload className="w-3.5 h-3.5 mr-1" /> {value.url ? 'Replace image' : 'Upload from device'}</>
          }
        </Button>
        {value.url && (
          <Button
            type="button"
            onClick={() => onChange({ url: '', alt: value.alt || '' })}
            className="btn-secondary text-xs py-2 px-3"
            data-testid={`${testIdPrefix}-remove-${imageKey}`}
          >
            Remove
          </Button>
        )}
      </div>

      <details className="text-xs">
        <summary className="text-text-light cursor-pointer select-none hover:text-primary">…or paste a URL instead</summary>
        <Input
          value={value.url}
          onChange={e => onChange({ url: e.target.value, alt: value.alt || '' })}
          placeholder="https://… image URL"
          className="input-field text-sm mt-2"
          data-testid={`${testIdPrefix}-url-${imageKey}`}
        />
      </details>

      <Input
        value={value.alt}
        onChange={e => onChange({ url: value.url || '', alt: e.target.value })}
        placeholder="Alt text (for SEO + accessibility)"
        className="input-field text-sm"
        data-testid={`${testIdPrefix}-alt-${imageKey}`}
      />
    </div>
  );
}

export default function AdminBlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const editSlug = searchParams.get('edit');
  const showCreate = searchParams.get('new') === '1';
  const navigate = useNavigate();
  const [showAiModal, setShowAiModal] = useState(false);
  const [cleaningMarkdown, setCleaningMarkdown] = useState(false);
  const retriedRef = useRef(new Set());

  useEffect(() => {
    loadPosts();
  }, []);

  // Self-heal: if URL points to a post we don't have in local state (e.g. just
  // generated via AI, direct bookmark, or background creation), re-fetch once.
  useEffect(() => {
    if (!editSlug || loading) return;
    const found = posts.find(p => p.slug === editSlug);
    if (!found && !retriedRef.current.has(editSlug)) {
      retriedRef.current.add(editSlug);
      setLoading(true);
      loadPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSlug, loading, posts]);

  const loadPosts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/blog/posts`);
      setPosts(data);
    } catch (err) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickTogglePublish = async (post) => {
    try {
      await axios.put(`${API_URL}/api/admin/blog/posts/${post.slug}`, { published: !post.published });
      toast.success(!post.published ? 'Post published' : 'Post unpublished');
      loadPosts();
    } catch {
      toast.error('Failed to update');
    }
  };

  if (editSlug) {
    const post = posts.find(p => p.slug === editSlug);
    if (loading) return (
      <PageLayout showNav={false}>
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      </PageLayout>
    );
    if (!post) return (
      <PageLayout showNav={false}>
        <div className="px-4 py-8 max-w-lg mx-auto text-center">
          <p className="text-text-secondary mb-4">Post not found.</p>
          <Link to="/admin/blog" className="text-primary font-semibold">Back to Blog Admin</Link>
        </div>
      </PageLayout>
    );
    return <EditBlogPost post={post} onSaved={loadPosts} />;
  }

  if (showCreate) {
    return <CreateBlogPost onCreated={(slug) => { loadPosts(); navigate(`/admin/blog?edit=${slug}`); }} />;
  }

  const handleCleanMarkdown = async () => {
    if (!window.confirm('Remove leftover ** / __ formatting markers from ALL blog posts? This is safe to run multiple times.')) return;
    setCleaningMarkdown(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/blog/clean-markdown`);
      if (data.cleaned_count === 0) {
        toast.success('All clean! No posts needed cleanup.');
      } else {
        toast.success(`Cleaned ${data.cleaned_count} post${data.cleaned_count === 1 ? '' : 's'}.`);
        loadPosts();
      }
    } catch {
      toast.error('Cleanup failed.');
    } finally {
      setCleaningMarkdown(false);
    }
  };

  return (
    <PageLayout showNav showUpgrade={false}>
      <div className="px-4 py-6 max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/admin" className="text-text-secondary hover:text-primary"><ArrowLeft className="w-5 h-5" /></Link>
          <h1 className="font-heading font-bold text-dark text-2xl flex-1">Blog Management</h1>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          <Link to="/admin/blog?new=1" className="btn-secondary px-4 py-2 text-sm flex items-center gap-1" data-testid="create-blog-btn">
            <Plus className="w-4 h-4" /> Write Blog Manually
          </Link>
          <button
            onClick={() => setShowAiModal(true)}
            className="btn-primary px-4 py-2 text-sm flex items-center gap-1"
            data-testid="generate-ai-blog-btn"
          >
            <Sparkles className="w-4 h-4" /> Generate AI Blog
          </button>
          <Link to="/admin/blog/ai-settings" className="text-xs text-primary hover:underline self-center" data-testid="ai-settings-link">
            AI Settings
          </Link>
          <button
            onClick={handleCleanMarkdown}
            disabled={cleaningMarkdown}
            className="text-xs text-text-light hover:text-primary self-center underline disabled:opacity-50"
            data-testid="clean-markdown-btn"
            title="Strip leftover ** / __ formatting markers from every saved blog post"
          >
            {cleaningMarkdown ? 'Cleaning…' : 'Clean ** from posts'}
          </button>
        </div>

        {showAiModal && (
          <AIBlogModal onClose={() => setShowAiModal(false)} onCreated={async (slug) => { setShowAiModal(false); await loadPosts(); navigate(`/admin/blog?edit=${slug}`); }} />
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-3">
            {posts.map(post => (
              <div key={post.slug} className="card-default p-4" data-testid={`admin-blog-${post.slug}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-dark text-sm truncate">{post.title}</h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${post.published ? 'bg-primary-light text-primary' : 'bg-gold-light text-gold'}`}
                        data-testid={`blog-status-${post.slug}`}
                      >
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <p className="text-text-light text-xs">/{post.slug}</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {['hero', 'mid1', 'mid2', 'bottom'].map(k => (
                        <span key={k} className={`text-xs px-2 py-0.5 rounded-full ${post.images?.[k]?.url ? 'bg-primary-light text-primary' : 'bg-gray-100 text-gray-400'}`}>
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="flex gap-2">
                      <Link to={`/blog/${post.slug}`} className="text-text-secondary hover:text-primary" title="View">
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                      <Link to={`/admin/blog?edit=${post.slug}`} className="text-primary hover:text-primary-dark font-semibold text-sm">
                        Edit
                      </Link>
                    </div>
                    <button
                      onClick={() => handleQuickTogglePublish(post)}
                      data-testid={`blog-quick-toggle-${post.slug}`}
                      className={`text-xs font-semibold px-3 py-1 rounded-full ${post.published ? 'bg-gray-100 text-text-secondary hover:bg-gray-200' : 'bg-primary text-white hover:bg-primary-hover'}`}
                    >
                      {post.published ? 'Unpublish' : 'Publish'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
}

function EditBlogPost({ post, onSaved }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({
    title: post.title || '',
    meta_title: post.meta_title || '',
    meta_description: post.meta_description || '',
    excerpt: post.excerpt || '',
    youtube_url: post.youtube_url || ''
  });
  const [cta, setCta] = useState({
    cta_title: post.cta_title || '',
    cta_description: post.cta_description || '',
    cta_button_text: post.cta_button_text || '',
    cta_url: post.cta_url || ''
  });
  const [images, setImages] = useState({
    hero: { url: post.images?.hero?.url || '', alt: post.images?.hero?.alt || '' },
    mid1: { url: post.images?.mid1?.url || '', alt: post.images?.mid1?.alt || '' },
    mid2: { url: post.images?.mid2?.url || '', alt: post.images?.mid2?.alt || '' },
    bottom: { url: post.images?.bottom?.url || '', alt: post.images?.bottom?.alt || '' }
  });
  const [saving, setSaving] = useState(false);

  const handleSaveMeta = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/blog/posts/${post.slug}`, meta);
      toast.success('Post metadata saved');
      onSaved();
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCta = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/blog/posts/${post.slug}`, cta);
      toast.success('CTA saved');
      onSaved();
    } catch (err) {
      toast.error('Failed to save CTA');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveImages = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/blog/posts/${post.slug}/images`, { images });
      toast.success('Images saved');
      onSaved();
    } catch (err) {
      toast.error('Failed to save images');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    setSaving(true);
    try {
      const newPublished = !post.published;
      await axios.put(`${API_URL}/api/admin/blog/posts/${post.slug}`, { published: newPublished });
      if (newPublished) {
        toast.success('Post published! It is now live on /blog.');
        toast.info('For ChatGPT / Claude / LinkedIn previews to see this post, redeploy from the chat (Save to GitHub → Deploy). Real users see it immediately.', { duration: 9000 });
      } else {
        toast.success('Post unpublished — back to draft.');
      }
      onSaved();
    } catch (err) {
      toast.error('Failed to update publish state');
    } finally {
      setSaving(false);
    }
  };

  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerateImage = async (imageKey) => {
    if (imageKey !== 'hero') {
      toast.info('AI image regeneration only supports the hero image right now.');
      return;
    }
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/blog/posts/${post.slug}/regenerate-image`, {}, { timeout: 180000 });
      setImages((prev) => ({ ...prev, hero: { ...prev.hero, url: data.image_url } }));
      toast.success('New hero image generated');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed';
      toast.error(typeof detail === 'string' ? detail : 'Failed');
    } finally {
      setRegenerating(false);
    }
  };

  const ImageField = ({ label, imageKey }) => (
    <div className="space-y-2">
      <BlogImageUploader
        imageKey={imageKey}
        label={label}
        value={images[imageKey]}
        onChange={(next) => setImages(prev => ({ ...prev, [imageKey]: next }))}
        slug={post.slug}
        testIdPrefix="blog-image"
      />
      {imageKey === 'hero' && (
        <Button
          onClick={() => handleRegenerateImage('hero')}
          disabled={regenerating}
          className="btn-secondary w-full text-sm"
          data-testid="blog-regenerate-hero-btn"
        >
          {regenerating ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Regenerating…</> : <><RefreshCw className="w-4 h-4 mr-1" /> Regenerate with AI</>}
        </Button>
      )}
    </div>
  );

  return (
    <PageLayout showNav={false}>
      <div className="px-4 py-6 max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/admin/blog')} className="text-text-secondary hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-bold text-dark text-xl flex-1 truncate">Edit: {post.title}</h1>
        </div>

        <Link to={`/blog/${post.slug}`} className="text-primary text-sm font-medium hover:underline mb-6 inline-flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> View live post
        </Link>

        {/* Publish status + toggle */}
        <div
          className={`mt-4 card-default p-4 flex items-center gap-3 ${post.published ? 'border-l-4 border-primary' : 'border-l-4 border-gold'}`}
          data-testid="blog-publish-status-card"
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-dark">
              {post.published ? 'Published' : 'Draft (not visible on public blog)'}
            </p>
            <p className="text-xs text-text-light mt-0.5">
              {post.published
                ? 'This post is live at /blog/' + post.slug
                : 'Click "Publish Now" to make this post visible at /blog.'}
            </p>
          </div>
          <Button
            onClick={handleTogglePublish}
            disabled={saving}
            className={`${post.published ? 'btn-secondary' : 'btn-primary'} px-4 py-2 text-sm flex-shrink-0`}
            data-testid="blog-publish-toggle-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (post.published ? 'Unpublish' : 'Publish Now')}
          </Button>
        </div>

        {/* Meta Section */}
        <section className="mt-6">
          <h2 className="font-heading font-semibold text-dark text-base mb-4">Post Details</h2>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-text-light font-medium">Title</label>
              <Input value={meta.title} onChange={e => setMeta(p => ({ ...p, title: e.target.value }))} className="input-field" data-testid="blog-edit-title" />
            </div>
            <div>
              <label className="text-xs text-text-light font-medium">Meta Title (SEO)</label>
              <Input value={meta.meta_title} onChange={e => setMeta(p => ({ ...p, meta_title: e.target.value }))} className="input-field" data-testid="blog-edit-meta-title" />
            </div>
            <div>
              <label className="text-xs text-text-light font-medium">Meta Description (SEO)</label>
              <textarea
                value={meta.meta_description}
                onChange={e => setMeta(p => ({ ...p, meta_description: e.target.value }))}
                rows={3}
                className="input-field w-full rounded-xl p-3 text-sm border border-border"
                data-testid="blog-edit-meta-desc"
              />
            </div>
            <div>
              <label className="text-xs text-text-light font-medium">Excerpt</label>
              <textarea
                value={meta.excerpt}
                onChange={e => setMeta(p => ({ ...p, excerpt: e.target.value }))}
                rows={2}
                className="input-field w-full rounded-xl p-3 text-sm border border-border"
                data-testid="blog-edit-excerpt"
              />
            </div>
            <div>
              <label className="text-xs text-text-light font-medium flex items-center gap-1"><Video className="w-3 h-3" /> YouTube Video URL</label>
              <Input
                value={meta.youtube_url}
                onChange={e => setMeta(p => ({ ...p, youtube_url: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=..."
                className="input-field"
                data-testid="blog-edit-youtube"
              />
              <p className="text-xs text-text-light mt-1">Paste a YouTube URL to embed a video in the post.</p>
            </div>
            <Button onClick={handleSaveMeta} disabled={saving} className="btn-primary w-full py-3" data-testid="blog-save-meta-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <><Save className="w-4 h-4 mr-2" /> Save Details</>}
            </Button>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-8">
          <h2 className="font-heading font-semibold text-dark text-base mb-4">Call to Action</h2>
          <p className="text-text-secondary text-xs mb-4">Customise the CTA banner at the bottom of the blog post. Leave blank for default ("Take Control of Your Payments").</p>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-text-light font-medium">CTA Title</label>
              <Input value={cta.cta_title} onChange={e => setCta(p => ({ ...p, cta_title: e.target.value }))} placeholder="e.g. Interested in Collaboration or Partnership Opportunity?" className="input-field" data-testid="blog-edit-cta-title" />
            </div>
            <div>
              <label className="text-xs text-text-light font-medium">CTA Description</label>
              <Input value={cta.cta_description} onChange={e => setCta(p => ({ ...p, cta_description: e.target.value }))} placeholder="e.g. Find out how proactive your organisation is..." className="input-field" data-testid="blog-edit-cta-desc" />
            </div>
            <div>
              <label className="text-xs text-text-light font-medium">CTA Button Text</label>
              <Input value={cta.cta_button_text} onChange={e => setCta(p => ({ ...p, cta_button_text: e.target.value }))} placeholder="e.g. Take the Assessment" className="input-field" data-testid="blog-edit-cta-btn-text" />
            </div>
            <div>
              <label className="text-xs text-text-light font-medium">CTA URL</label>
              <Input value={cta.cta_url} onChange={e => setCta(p => ({ ...p, cta_url: e.target.value }))} placeholder="e.g. https://www.qualiquiz.app/quiz/..." className="input-field" data-testid="blog-edit-cta-url" />
              <p className="text-xs text-text-light mt-1">Use full URL for external links (https://...) or /auth for internal sign-up.</p>
            </div>
            <Button onClick={handleSaveCta} disabled={saving} className="btn-primary w-full py-3" data-testid="blog-save-cta-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <><Save className="w-4 h-4 mr-2" /> Save CTA</>}
            </Button>
          </div>
        </section>

        {/* Images Section */}
        <section className="mt-8">
          <h2 className="font-heading font-semibold text-dark text-base mb-4">Images</h2>
          <p className="text-text-secondary text-xs mb-4">Paste image URLs below. Upload images to Emergent first, then paste the public URL here.</p>
          <div className="space-y-4">
            <ImageField label="Hero Image (Top of Article)" imageKey="hero" />
            <ImageField label="Mid-Content Image 1 (Problem Section)" imageKey="mid1" />
            <ImageField label="Mid-Content Image 2 (Solution Section)" imageKey="mid2" />
            <ImageField label="Bottom Image (CTA Section)" imageKey="bottom" />
            <Button onClick={handleSaveImages} disabled={saving} className="btn-primary w-full py-3" data-testid="blog-save-images-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <><Save className="w-4 h-4 mr-2" /> Save All Images</>}
            </Button>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}


function CreateBlogPost({ onCreated }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    slug: '',
    meta_title: '',
    meta_description: '',
    excerpt: '',
    youtube_url: '',
    published: false
  });
  const [contentText, setContentText] = useState('');
  const [faqText, setFaqText] = useState('');
  const [images, setImages] = useState({
    hero: { url: '', alt: '' },
    mid1: { url: '', alt: '' },
    mid2: { url: '', alt: '' },
    bottom: { url: '', alt: '' }
  });
  const [saving, setSaving] = useState(false);
  const [slugTaken, setSlugTaken] = useState(false);

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  // Live slug-availability check (debounced via the input change event)
  const checkSlugTaken = async (slug) => {
    if (!slug) { setSlugTaken(false); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/blog/posts`);
      setSlugTaken(data.some(p => p.slug === slug));
    } catch { /* ignore */ }
  };

  const parseContent = (text) => {
    if (!text.trim()) return [];
    const blocks = [];
    for (const line of text.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (trimmed.startsWith('## ')) blocks.push({ type: 'heading', level: 2, text: trimmed.slice(3) });
      else if (trimmed.startsWith('### ')) blocks.push({ type: 'heading', level: 3, text: trimmed.slice(4) });
      else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const last = blocks[blocks.length - 1];
        if (last?.type === 'list') last.items.push(trimmed.slice(2));
        else blocks.push({ type: 'list', items: [trimmed.slice(2)] });
      }
      else if (trimmed.startsWith('[image:')) {
        const key = trimmed.match(/\[image:(\w+)\]/)?.[1];
        if (key) blocks.push({ type: 'image', key });
      }
      else blocks.push({ type: 'text', text: trimmed });
    }
    return blocks;
  };

  const parseFaqs = (text) => {
    if (!text.trim()) return [];
    const faqs = [];
    const lines = text.split('\n').filter(l => l.trim());
    for (let i = 0; i < lines.length; i += 2) {
      if (lines[i] && lines[i + 1]) {
        faqs.push({
          question: lines[i].replace(/^Q:\s*/i, '').trim(),
          answer: lines[i + 1].replace(/^A:\s*/i, '').trim()
        });
      }
    }
    return faqs;
  };

  const handleCreate = async (publish = false) => {
    if (!form.title || !form.slug) { toast.error('Title and slug are required'); return; }
    if (slugTaken) {
      toast.error('A post with this slug already exists. Please change the URL slug.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        published: publish,
        content: parseContent(contentText),
        faqs: parseFaqs(faqText),
        images
      };
      const { data } = await axios.post(`${API_URL}/api/admin/blog/posts`, payload);
      toast.success(publish ? 'Blog post published!' : 'Draft saved!');
      onCreated(data.slug);
    } catch (err) {
      // Surface the real reason whenever possible
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).filter(Boolean).join('. ') || 'Failed to create post');
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Your admin session has expired. Please sign in again.');
      } else if (!err.response) {
        toast.error('Network error — please check your connection and try again.');
      } else {
        toast.error(`Failed to create post (HTTP ${err.response.status}). Try again or change the URL slug.`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout showNav={false}>
      <div className="px-4 py-6 max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/admin/blog')} className="text-text-secondary hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-bold text-dark text-2xl">Create Blog Post</h1>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-text-light font-medium">Title</label>
            <Input
              value={form.title}
              onChange={e => {
                const newSlug = form.slug || autoSlug(e.target.value);
                setForm(p => ({ ...p, title: e.target.value, slug: newSlug }));
                if (!form.slug) checkSlugTaken(newSlug);
              }}
              placeholder="e.g. Why Banks Don't Warn You"
              className="input-field"
              data-testid="blog-create-title"
            />
          </div>
          <div>
            <label className="text-xs text-text-light font-medium">URL Slug</label>
            <Input
              value={form.slug}
              onChange={e => {
                const s = autoSlug(e.target.value);
                setForm(p => ({ ...p, slug: s }));
                checkSlugTaken(s);
              }}
              placeholder="why-banks-dont-warn-you"
              className={`input-field ${slugTaken ? 'border-alert' : ''}`}
              data-testid="blog-create-slug"
            />
            <p className={`text-xs mt-1 ${slugTaken ? 'text-alert font-semibold' : 'text-text-light'}`} data-testid="blog-slug-status">
              {slugTaken
                ? `⚠️ A post with this slug already exists. Please change it.`
                : `URL: /blog/${form.slug || '...'}`}
            </p>
          </div>
          <div>
            <label className="text-xs text-text-light font-medium">Meta Title (SEO)</label>
            <Input value={form.meta_title} onChange={e => setForm(p => ({ ...p, meta_title: e.target.value }))} className="input-field" />
          </div>
          <div>
            <label className="text-xs text-text-light font-medium">Meta Description (SEO)</label>
            <textarea value={form.meta_description} onChange={e => setForm(p => ({ ...p, meta_description: e.target.value }))} rows={2} className="input-field w-full rounded-xl p-3 text-sm border border-border" />
          </div>
          <div>
            <label className="text-xs text-text-light font-medium">Excerpt</label>
            <textarea value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} rows={2} className="input-field w-full rounded-xl p-3 text-sm border border-border" />
          </div>
          <div>
            <label className="text-xs text-text-light font-medium flex items-center gap-1"><Video className="w-3 h-3" /> YouTube Video URL</label>
            <Input value={form.youtube_url} onChange={e => setForm(p => ({ ...p, youtube_url: e.target.value }))} placeholder="https://www.youtube.com/watch?v=..." className="input-field" />
          </div>
          <div>
            <label className="text-xs text-text-light font-medium">Content (Markdown-like)</label>
            <textarea
              value={contentText}
              onChange={e => setContentText(e.target.value)}
              rows={12}
              placeholder={"Write your blog content here...\n\n## Section Heading\n\nParagraph text goes here.\n\n- Bullet point 1\n- Bullet point 2\n\n[image:hero]\n[image:mid1]\n[image:mid2]\n[image:bottom]"}
              className="input-field w-full rounded-xl p-3 text-sm border border-border font-mono"
              data-testid="blog-create-content"
            />
            <p className="text-xs text-text-light mt-1">Use ## for H2, ### for H3, - for bullets, [image:hero] [image:mid1] [image:mid2] [image:bottom] for image placeholders.</p>
          </div>
          <div>
            <label className="text-xs text-text-light font-medium">FAQs (Q&A pairs, alternating lines)</label>
            <textarea
              value={faqText}
              onChange={e => setFaqText(e.target.value)}
              rows={6}
              placeholder={"Q: How does this work?\nA: It works by tracking your payments.\nQ: Is it free?\nA: Yes, free to start."}
              className="input-field w-full rounded-xl p-3 text-sm border border-border font-mono"
              data-testid="blog-create-faqs"
            />
            <p className="text-xs text-text-light mt-1">Alternate Q: and A: lines. Each pair becomes an FAQ entry.</p>
          </div>

          {/* Images Section — set on create */}
          <div className="pt-2">
            <h3 className="font-heading font-semibold text-dark text-sm mb-1 flex items-center gap-1">
              <ImageIcon className="w-4 h-4 text-primary" /> Images (optional)
            </h3>
            <p className="text-xs text-text-light mb-3">
              Paste public image URLs. Use <code className="bg-gray-100 px-1 rounded">[image:hero]</code>, <code className="bg-gray-100 px-1 rounded">[image:mid1]</code>, <code className="bg-gray-100 px-1 rounded">[image:mid2]</code>, <code className="bg-gray-100 px-1 rounded">[image:bottom]</code> in your content where each should appear.
            </p>
            <div className="space-y-3">
              {[
                { key: 'hero', label: 'Hero (top of article)' },
                { key: 'mid1', label: 'Mid-content 1' },
                { key: 'mid2', label: 'Mid-content 2' },
                { key: 'bottom', label: 'Bottom (CTA section)' },
              ].map(({ key, label }) => (
                <BlogImageUploader
                  key={key}
                  imageKey={key}
                  label={label}
                  value={images[key]}
                  onChange={(next) => setImages(p => ({ ...p, [key]: next }))}
                  slug={form.slug}
                  testIdPrefix="blog-create-image"
                />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <Button
              onClick={() => handleCreate(false)}
              disabled={saving}
              className="btn-secondary w-full py-3"
              data-testid="blog-create-draft-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <><Save className="w-4 h-4 mr-2" /> Save as Draft</>}
            </Button>
            <Button
              onClick={() => handleCreate(true)}
              disabled={saving}
              className="btn-primary w-full py-3"
              data-testid="blog-create-publish-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <><Plus className="w-4 h-4 mr-2" /> Save & Publish</>}
            </Button>
          </div>
          <p className="text-xs text-text-light text-center" data-testid="blog-create-helper">
            Drafts are not visible on the public blog. You can publish later from the Edit screen.
          </p>
        </div>
      </div>
    </PageLayout>
  );
}

function AIBlogModal({ onClose, onCreated }) {
  const [mode, setMode] = useState('random');
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('personal');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    if (mode === 'keyword' && !topic.trim()) {
      toast.error('Please enter a topic or keyword');
      return;
    }
    setGenerating(true);
    try {
      const payload = mode === 'keyword'
        ? { topic: topic.trim(), audience, source: 'keyword' }
        : { source: 'manual_button' };
      const { data } = await axios.post(`${API_URL}/api/admin/blog/generate`, payload, { timeout: 240000 });
      toast.success(`AI draft created: ${data.title}`);
      toast.info('Draft saved (unpublished). Review it, then click "Publish" — and redeploy when you want AI search engines (ChatGPT, Claude) to pick it up.', { duration: 9000 });
      onCreated(data.slug);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Generation failed';
      toast.error(typeof detail === 'string' ? detail : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="ai-blog-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-primary" />
          <h2 className="font-heading font-bold text-dark text-lg">Generate AI Blog</h2>
        </div>
        <p className="text-sm text-text-secondary mb-4">
          Creates a draft (never auto-publishes). You can edit, regenerate or publish on the next screen.
        </p>

        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => setMode('random')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${mode === 'random' ? 'bg-primary text-white border-primary' : 'bg-white text-dark border-border'}`}
            data-testid="ai-mode-random"
          >
            🎲 Random topic
          </button>
          <button
            type="button"
            onClick={() => setMode('keyword')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold border ${mode === 'keyword' ? 'bg-primary text-white border-primary' : 'bg-white text-dark border-border'}`}
            data-testid="ai-mode-keyword"
          >
            ✏️ From keyword
          </button>
        </div>

        {mode === 'random' && (
          <p className="text-xs text-text-light mb-4 px-3 py-2 bg-gray-50 rounded-lg">
            AI will pick a random unused topic from your library (40+ topics across personal money, FCA / Consumer Duty, dementia & carers, debt prevention, smart reminder technology…).
          </p>
        )}

        {mode === 'keyword' && (
          <>
            <label className="text-xs text-text-light font-medium">Topic or keyword</label>
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. How payday loans hurt your credit score"
              className="input-field mb-3"
              data-testid="ai-topic-input"
            />
            <label className="text-xs text-text-light font-medium">Audience</label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="input-field w-full mb-3"
              data-testid="ai-audience-select"
            >
              <option value="personal">Personal (consumers)</option>
              <option value="authority">Authority (banks / fintech / FCA / charities)</option>
            </select>
          </>
        )}

        <div className="flex gap-2 mt-2">
          <Button
            onClick={onClose}
            className="btn-secondary flex-1"
            data-testid="ai-cancel-btn"
            disabled={generating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="btn-primary flex-1"
            data-testid="ai-generate-btn"
          >
            {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Generating…</> : 'Generate Draft'}
          </Button>
        </div>
        <p className="text-xs text-text-light text-center mt-3">
          Takes 30-90 seconds. Don't close this window.
        </p>
      </div>
    </div>
  );
}

